import { AwsUi } from '../../../core/runtime/src';
import BemClassBuilder from '../../../core/utils/src/class-builders/bem';
import { Form } from '../../form/src';
import { Button } from '../../button/src';
export var Wizard = AwsUi.__addComponent('awsui-wizard', {
    releaseStatus: 'stable',
    properties: {
        // Array of step objects. Each object represents a step in the wizard with the following properties:
        // - `title` [string]: Text that serves as the title in the navigation pane as well as in the form header.
        // - `info` [Renderable]: Optional area for a page level info link displayed in the form header. The page level info link triggers the default help panel content for the step.
        // - `description` [Renderable]: Optional area below the form header for a page level descriptive text to further explain the purpose, goal, or main actions of the step.
        // - `content` [Renderable]: Main content area to display form sections, form fields, and controls.
        // - `errorText` [Renderable]: Optional error text displayed in a page level error alert. Use this for rendering actionable, server-side validation failure messages.
        // - `isOptional` [boolean]: Whether the step is optional or required. If set to true, the text from `i18nStrings.optional` is rendered next to the navigation step label and the form header title.
        steps: {
            type: 'array',
            itemTypes: ['Wizard.Step']
        },
        // Index of the active step or the step that is currently being displayed. The first step has index 0, which is also the default value.
        activeStepIndex: {
            type: 'integer',
            defaultValue: 0,
            onChange: function (oldValue, newValue) {
                this.__farthestStepIndex = Math.max(this.__farthestStepIndex, newValue);
            }
        },
        // An object containing all the necessary localized strings required by the component.
        // - `stepNumberLabel`: `(stepNumber: integer): string` A function that accepts a number (1-based indexing), and returns a human-readable, localized string displaying the step number in the navigation pane. *For example, "Step 1" or "Step 2"*.
        // - `collapsedStepsLabel`: `(stepNumber, stepsCount): string` A function that accepts two integer (1-based indexing) parameters, and returns a string responsible for the navigation summary on smaller screens. *For example: "Step 1 of 3"*. Accepted parameters are:
        //    - `stepNumber` [integer]: The step number that the user is currently on.
        //    - `stepsCount` [integer]: The total number of steps in the wizard.
        // - `cancelButton` [string]: The text rendered on the button that allows the user to *exit* the flow.
        // - `previousButton` [string]: The text rendered on the button that allows the user to return to the *previous* step.
        // - `nextButton` [string]: The text rendered on the button that allows the user to move to the *next* step.
        // - `submitButton` [string]: The text rendered on the button that allows the user to *submit* the form.
        // - `optional` [string]: The text rendered next to the step title and form header title, if a step is declared optional. Do not include the "-".
        i18nStrings: {
            type: 'object',
            objectType: 'Wizard.I18nStrings'
        },
        // Renders the *next* or *submit* button in a loading state.
        // Use this if you need to wait for a response from the server before the user can proceed to the next step, such as during server-side validation or retrieving the next step's information.
        isLoadingNextStep: {
            type: 'boolean'
        },
        // Highest step index that has been visited by the user. This is used to determine which steps should be enabled and which should be disabled.
        // Unvisited steps should always be disabled.
        __farthestStepIndex: {
            type: 'integer'
        }
    },
    events: {
        // Fired when a user clicks the *cancel* button.
        // If a user has entered data in the form, prompt the user with a modal before exiting the wizard flow.
        // See the [unsaved changes pattern](/design/patterns/unsaved_changes/) for more details.
        cancelButtonClick: {
            bubbles: true,
            cancelable: false
        },
        // Fired when a user clicks the *previous* button.
        // The event `detail` includes the index of the requested step, which in this case, is one less than the activeStepIndex.
        //
        // By default, this event decreases activeStepIndex by one and scrolls to the top of the requested page.
        previousButtonClick: {
            bubbles: true,
            cancelable: true,
            detailType: 'Wizard.StepClickDetail'
        },
        // Fired when a user clicks the *next* button.
        // The event `detail` includes the index of the requested step, which in this case, is one more than the activeStepIndex.
        //
        // By default, this event increases activeStepIndex by one and scrolls to the top of the requested page.
        nextButtonClick: {
            bubbles: true,
            cancelable: true,
            detailType: 'Wizard.StepClickDetail'
        },
        // Fired when a user clicks the *submit* button.
        submitButtonClick: {
            bubbles: true,
            cancelable: false
        },
        // Fired when a user clicks an enabled step link in the navigation pane.
        // The event `detail` includes the index of the requested step.
        //
        // By default, this event sets the activeStepIndex equal to the requestedStepIndex and scrolls to the top of the requested page.
        stepNavigationClick: {
            bubbles: true,
            cancelable: true,
            detailType: 'Wizard.StepClickDetail'
        }
    },
    callbacks: {
        initialized: function () {
            var overflowRegex = /(auto|scroll)/;
            var parent = this.node.parentElement;
            while (parent && !overflowRegex.test(getComputedStyle(parent).overflow)) {
                parent = parent.parentElement;
            }
            this.scrollParent = parent;
            this.__farthestStepIndex = this.activeStepIndex;
        }
    },
    functions: {
        // activeStepIndex will not exceed the maximum available index by clicking Next button at the last step
        __setActiveStepIndex: function (stepIndex) {
            var newIndex = stepIndex >= this.steps.length ? this.steps.length - 1 : stepIndex;
            this.activeStepIndex = newIndex;
        },
        __hasVisited: function (stepIndex) {
            return this.__farthestStepIndex >= stepIndex;
        },
        __isActiveStep: function (stepIndex) {
            return stepIndex === this.activeStepIndex;
        },
        __getActiveStep: function () {
            return this.steps[this.activeStepIndex] || {};
        },
        __fireStepClickEvent: function (eventKey, requestedStepIndex) {
            var _this = this;
            this.__fireEvent(eventKey, { requestedStepIndex: requestedStepIndex }, {
                onNotCanceled: function () {
                    _this.__setActiveStepIndex(requestedStepIndex);
                    if (_this.scrollParent) {
                        _this.scrollParent.scrollTop = 0;
                    }
                    else {
                        window.scrollTo(window.pageXOffset, 0);
                    }
                }
            });
        },
        __onCancelButtonClick: function () {
            this.__fireEvent('cancelButtonClick');
        },
        __onPreviousButtonClick: function () {
            var requestedPageIndex = this.activeStepIndex - 1;
            this.__fireStepClickEvent('previousButtonClick', requestedPageIndex);
        },
        __onNextButtonClick: function () {
            var requestedPageIndex = this.activeStepIndex + 1;
            this.__fireStepClickEvent('nextButtonClick', requestedPageIndex);
        },
        __onSubmitButtonClick: function () {
            this.__fireEvent('submitButtonClick');
        },
        __onStepNavigationClick: function (event, requestedPageIndex) {
            // Prevent anchor navigation
            event.preventDefault();
            this.__fireStepClickEvent('stepNavigationClick', requestedPageIndex);
        }
    },
    render: function () {
        var _this = this;
        var i18nStrings = this.i18nStrings || {};
        var optionalTextWithDash = " - " + i18nStrings.optional;
        var getOptionalTextElement = function () { return AwsUi.__createElement("i", null, optionalTextWithDash); };
        var getNavigation = function () { return (AwsUi.__createElement("nav", { className: "awsui-util-pt-xs " + new BemClassBuilder(_this, 'column-navigation').toString() },
            AwsUi.__createElement("ul", { className: "awsui-list-unstyled " + new BemClassBuilder(_this, 'navigation-links').toString() }, _this.steps.map(function (_a, i) {
                var title = _a.title, isOptional = _a.isOptional;
                var isActiveStep = _this.__isActiveStep(i);
                return (AwsUi.__createElement("li", { key: i, className: new BemClassBuilder(_this, 'navigation-link-item') },
                    AwsUi.__createElement("small", null,
                        i18nStrings.stepNumberLabel && i18nStrings.stepNumberLabel(i + 1),
                        isOptional && getOptionalTextElement()),
                    AwsUi.__createElement("div", null, _this.__hasVisited(i) && !isActiveStep ? (AwsUi.__createElement("a", { href: "#", className: new BemClassBuilder(_this, 'navigation-link').toString(), onclick: function (event) { return _this.__onStepNavigationClick(event, i); } }, title)) : (AwsUi.__createElement("span", { className: new BemClassBuilder(_this, 'navigation-link')
                            .modifier(isActiveStep ? 'active' : 'disabled')
                            .toString(), "aria-current": isActiveStep && 'step', "aria-disabled": "true" }, title)))));
            })))); };
        var getForm = function () {
            var _a = _this.__getActiveStep(), title = _a.title, info = _a.info, description = _a.description, content = _a.content, errorText = _a.errorText, isOptional = _a.isOptional;
            var primaryButtonText = _this.activeStepIndex !== _this.steps.length - 1 ? i18nStrings.nextButton : i18nStrings.submitButton;
            var activeStepInfoLink = info ? _this.renderToMithril("active-step-info-" + _this.activeStepIndex, info) : null;
            var activeStepDescription = description
                ? _this.renderToMithril("active-step-description-" + _this.activeStepIndex, description)
                : null;
            var activeStepContent = content
                ? _this.renderToMithril("active-step-content-" + _this.activeStepIndex, content)
                : null;
            var activeStepErrorText = errorText
                ? _this.renderToMithril("active-step-error-text-" + _this.activeStepIndex, errorText)
                : null;
            return (AwsUi.__createElement("div", { className: new BemClassBuilder(_this, 'column-form').toString() },
                AwsUi.__createElement(Form, { header: AwsUi.__createElement("div", null,
                        AwsUi.__createElement("div", { className: new BemClassBuilder(_this, 'step-number').toString() },
                            AwsUi.__createElement("p", { className: "awsui-text-secondary" }, i18nStrings.collapsedStepsLabel &&
                                i18nStrings.collapsedStepsLabel(_this.activeStepIndex + 1, _this.steps.length))),
                        AwsUi.__createElement("h1", { className: "awsui-util-d-i" },
                            title,
                            isOptional && getOptionalTextElement()),
                        AwsUi.__createElement("span", { className: new BemClassBuilder(_this, 'info').toString() }, activeStepInfoLink)), actions: AwsUi.__createElement("div", null,
                        AwsUi.__createElement(Button, { className: new BemClassBuilder(_this, 'cancel-button').toString(), text: i18nStrings.cancelButton, variant: "link", formAction: "none", click: _this.__onCancelButtonClick.bind(_this) }),
                        _this.activeStepIndex !== 0 && (AwsUi.__createElement(Button, { className: new BemClassBuilder(_this, 'previous-button').toString(), text: i18nStrings.previousButton, click: _this.__onPreviousButtonClick.bind(_this), formAction: "none" })),
                        AwsUi.__createElement(Button, { className: new BemClassBuilder(_this, 'primary-button').toString(), text: primaryButtonText, variant: "primary", formAction: "none", click: _this.activeStepIndex < _this.steps.length - 1
                                ? _this.__onNextButtonClick.bind(_this)
                                : _this.__onSubmitButtonClick.bind(_this), loading: _this.isLoadingNextStep, disabled: _this.isLoadingNextStep })), description: activeStepDescription, content: activeStepContent, errorText: activeStepErrorText })));
        };
        return (AwsUi.__createElement("div", { className: "awsui-wizard" },
            getNavigation(),
            getForm()));
    }
});
//# sourceMappingURL=index.js.map